// Importing main React and ReactDOM dependencies for UI handling.
import React from 'react';
import { createRoot } from 'react-dom/client';

// Import functionality for monitoring application performance.
import reportWebVitals from './reportWebVitals';

// Importing the root component of the application.
import App from './App';

// Importing global styles for the application.
import './index.scss';
import 'antd/dist/reset.css';

// Importing dependencies for handling routing in the React application.
import { BrowserRouter } from 'react-router-dom';

// Importing necessary dependencies for working with the Redux store and its persistent integration.
import { store, persistor } from './store/store';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { YMInitializer } from 'react-yandex-metrika';

// --- Company config ---
import { companies } from './configs/companies';

const currentCompany = process.env.REACT_APP_COMPANY_MODE || 'default';
const APP_CONFIG = companies[currentCompany as keyof typeof companies];

const applyIconLink = (link: HTMLLinkElement | null, href: string) => {
  if (!link) return;
  link.href = href;
  if (href.endsWith('.svg')) {
    link.type = 'image/svg+xml';
  } else if (href.endsWith('.ico')) {
    link.type = 'image/x-icon';
  } else {
    link.removeAttribute('type');
  }
};

applyIconLink(document.getElementById('favicon') as HTMLLinkElement, APP_CONFIG.favicon);
applyIconLink(document.getElementById('favicon-shortcut') as HTMLLinkElement, APP_CONFIG.favicon);
applyIconLink(document.getElementById('apple-icon') as HTMLLinkElement, APP_CONFIG.appleIcon);
document.title = APP_CONFIG.siteName;

const metaAppName = document.querySelector('meta[name="application-name"]') as HTMLMetaElement;
if (metaAppName) metaAppName.content = APP_CONFIG.siteName;

const metaOgSite = document.querySelector('meta[property="og:site_name"]') as HTMLMetaElement;
if (metaOgSite) metaOgSite.content = APP_CONFIG.siteName;

// Getting a reference to the root DOM element.
const rootElement = document.getElementById('root') as HTMLElement;
// Creating a root React instance to manage the application.
const root = createRoot(rootElement);

// env
const IS_DEV = process.env.NODE_ENV !== 'production';
const METRIKA_ID = Number(process.env.REACT_APP_METRIKA_ID);
const ENABLE_METRIKA = !IS_DEV && Number.isFinite(METRIKA_ID) && METRIKA_ID > 0;

// Rendering the React application in the DOM, wrapping App in the necessary providers.
root.render(
  <React.StrictMode>
    {ENABLE_METRIKA && (
      <YMInitializer
        accounts={[METRIKA_ID]}
        options={{
          defer: true,
          webvisor: true,
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          ecommerce: 'dataLayer',
        }}
        version="2"
      />
    )}
    {/* React.StrictMode to detect potential problems */}
    <Provider store={store}>
      {/* Providing the Redux store to the entire application */}
      <PersistGate loading={null} persistor={persistor}>
        {/* Delaying UI load until Redux state is restored */}
        <BrowserRouter>
          {/* Ensuring routing support */}
          <App /> {/* The root component of the application */}
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);

// Launching the performance monitoring of the application.
reportWebVitals();
